import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Modal,
  TextField,
  Grid,
  FormControl,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Paper, Box, Checkbox, Avatar,
  CircularProgress,
} from '@material-ui/core';
import { ExpandLess, ExpandMore, CameraAlt } from "@material-ui/icons";
import { formatNumber } from "../../../../helpers/utils";
import SingleAsyncAutoComplete from "../../shared/SingleAsyncAutoComplete";
import FilterAutoComplete from "../../shared/FilterAutoComplete";
import AdminEditContactContainer from "../../../../containers/admin/AdminEditContactContainer";
import CustomSnackbar from "../../../user/shared/CustomSnackbar";
import ChangeLogModal from "../../shared/ChangeLogModal";
import _ from "lodash";
import { CurrencyFormatter } from "../../shared/Formatters";
import TextInput from "../../sharedFormComponents/TextInput";
import UrlTextField from "../../shared/Inputs/UrlTextField";
import WarningsSection from "../WarningsSection";
import { NavLink } from "react-router-dom";
import { ArrowBack } from "@material-ui/icons";
import { ChipLabel } from "../../shared/Chips";
import UnifiedTeamMemberRow from "../components/UnifiedTeamMemberRow";
import DuplicateTeamsWarning from "./DuplicateTeamsWarning";
import PwtMarkAsInactiveModal from "../modals/PwtMarkAsInactiveModal";
import MigrateModal from "../modals/MigrateModal";
import NotesDialog from "../../shared/Dialogs/NotesDialog";
import { DropzoneArea } from "material-ui-dropzone";
import TeamMerger from "./TeamMerger";
import AccoladesSection from "../AccoladesSection";
import LocationsSection from "../LocationsSection";
import ActiveChip from "../../shared/Chips/ActiveChip";
import RegenerateBio from "../../shared/Buttons/RegenerateBio";
import Tooltip from "@material-ui/core/Tooltip";
import PwgRejectedDialog from "../../shared/Dialogs/PwgRejectedDialog";
import TeamLocationMerger from "./TeamLocationMerger";
import PreDraftContactsSection from "../PreDraftContactsSection";
import AumAggregationsSection from "../AumAggregationsSection";
import TeamLocationAddReplace from "./TeamLocationAddReplace";
import PrivateWealthTeamNews from "../PrivateWealthTeamNews";
import HistoricalBlueprintsSection from "../HistoricalBlueprintsSection";
import MergeRelations from "./MergeRelations";
const MATransactionForm = React.lazy(() => import("../../maValidations/form/MATransactionForm"));
import usePrivateWealthTeamForm from '../hooks/usePrivateWealthTeamForm';
import ConfirmDialog from '../../shared/Dialogs/ConfirmDialog';

const DUPLICATE_LOCATION_ERROR = 'Team has duplicated locations';
const VALIDATE_LOCATION_ERROR = 'Locations must have fields filled out';
const MISSING_PRINCIPAL_ERROR = 'At least one location must be marked as principal';
const VALIDATE_PRE_DRAFT_LOCATION_ERROR = 'The location can only be associated with a PWG if all associated contacts work there';

const DUPLICATE_HISTORICAL_BLUEPRINT_ERROR = 'Team has duplicated historical blueprints';
const VALIDATE_HISTORICAL_BLUEPRINT_ERROR = 'Historical Blueprints must have fields filled out';

// Salesforce's standard Name field on custom objects caps at 80 chars; longer team names fail to sync.
const TEAM_NAME_MAX_LENGTH = 80;

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: '5%',
    left: '5%',
    outline: 'none',
    boxShadow: theme.shadows[20],
    height: '90%',
    overflowY: 'auto',
    width: '90%'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actions: {
    justifyContent: 'flex-end'
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttonMarginRight: {
    marginRight: 10,
  },
  buttonLabel: {
    textTransform: 'capitalize',
  },
  cardHeaderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))

const headTeamMemberCells = [
  { id: 'first_name', label: 'First Name' },
  { id: 'last_name', label: 'Last Name' },
  { id: 'ria_contact_crd_id', label: 'CRD' },
  { id: 'ria_contact_current_company', label: 'Current Firms', nonSortable: true },
];

const headFormerTeamMemberCells = [
  { id: 'ria_contact_crd_id', label: 'CRD' },
  { id: 'first_name', label: 'First Name' },
  { id: 'last_name', label: 'Last Name' },
  { id: 'ria_contact_current_company', label: 'Current Firms', nonSortable: true },
  { id: 'active', label: 'Active' },
  { id: 'end_date', label: 'End Date' },
];

const headTeamPotentialCells = [
  { id: 'contact_first_name', label: 'First Name' },
  { id: 'contact_last_name', label: 'Last Name' },
  { id: 'contact_crd_id', label: 'CRD' },
];

const PrivateWealthTeamForm = ({
  session,
  team,
  activeData,
  onClose,
  open,
  afterCreate,
  onDataUpdate,
  itemIndex,
  goBack,
  fetchPrivateWealthTeams,
  itemsInQueue,
  hasQueue,
  snackbar,
  draftData,
  fromWarnings,
  onChangeNotes
}) => {
  const classes = useStyles();

  const {
    // Confirm Dialog hook states and handlers.
    confirmDialog,
    closeConfirmDialog,

    // Aum Aggregations hook states and handlers.
    aumAggregations,
    fetchingAumAggregations,
    handleChangeAumAggregation,
    handleBlurAumAggregation,
    handleAddAumAggregation,
    handleRemoveAumAggregation,

    // Accolades hook states and handlers.
    accolades,
    fetchingAccolades,
    excludedAccolades,

    // Firm Info hook states and handlers.
    getFirmAumAggregationInfo,

    fetchExcludedAccolades,
    handleAddAccoladeToTeam,
    handleRemoveAccoladeFromTeam,

    // MA Transactions hook states and handlers.
    selectedMaTransactionId,
    openMaTransactionModal,

    setSelectedMaTransactionId,
    handleOpenMaTransaction,
    handleCloseMaTransaction,

    // Estados básicos
    teamFirmInfo,
    setTeamFirmInfo,
    teamMembers,
    setTeamMembers,
    formerTeamMembers,
    setFormerTeamMembers,
    potentialTeamMembers,
    setPotentialTeamMembers,

    // Estados de selección de opciones
    selectedSpecialties,
    setSelectedSpecialties,
    selectedExcludedSpecialties,
    setExcludedSelectedSpecialties,
    selectedExcludedInvestmentUtilized,
    setExcludedSelectedInvestmentUtilized,
    selectedExcludedClientTypes,
    setExcludedSelectedClientTypes,
    selectedClientTypes,
    setSelectedClientTypes,
    selectedInvestmentUtilized,
    setSelectedInvestmentUtilized,

    // Estados de expansión
    expandFirmInfo,
    setExpandFirmInfo,
    expandTeamMembers,
    setExpandTeamMembers,
    expandPotentialTeamMembers,
    setExpandPotentialTeamMembers,

    // Estados de ordenamiento
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    formerSortBy,
    setFormerSortBy,
    potentialSortBy,
    setPotentialSortBy,
    formerSortDirection,
    setFormerSortDirection,
    potentialSortDirection,
    setPotentialSortDirection,

    // Estados de paginación
    totalItems,
    setTotalItems,
    totalItemsFormer,
    setTotalItemsFormer,
    totalItemsPotential,
    setTotalItemsPotential,
    initialTotalItems,
    setInitialTotalItems,
    initialTotalItemsFormer,
    setInitialTotalItemsFormer,
    currentPage,
    setCurrentPage,
    currentPageFormer,
    setCurrentPageFormer,
    currentPagePotential,
    setCurrentPagePotential,
    rowsPerPage,
    setRowsPerPage,
    rowsPerPageFormer,
    setRowsPerPageFormer,
    rowsPerPagePotential,
    setRowsPerPagePotential,

    // Estados de contactos
    selectedContact,
    setSelectedSelectedContact,
    selectedContactCrdId,
    setSelectedContactCrdId,
    openContactModal,
    setOpenContactModal,

    // Estados de errores y mensajes
    errorMessage,
    setErrorMessage,
    linkAction,
    setLinkAction,
    linkText,
    setLinkText,
    status,
    setStatus,
    showSnackBar,
    setShowSnackBar,

    // Estados de cambios y modales
    changes,
    setChanges,
    openChanges,
    setOpenChanges,
    openMarkAsInactiveModal,
    setOpenMarkAsInactiveModal,
    openPreview,
    setOpenPreview,
    openModal,
    setOpenModal,

    // Estados de warnings y accolades
    warnings,
    setWarnings,
    fetchingWarnings,
    setFetchingWarnings,
    news,
    setNews,
    newsTypes,
    setNewsTypes,
    riaInvestorParent,
    setRiaInvestorParent,
    fetchingNews,
    setFetchingNews,

    // Estados de cambios
    hasChanges,
    setHasChanges,
    isFromUserChange,
    setIsFromUserChange,

    // Estados de merge
    relatedInvestorsOptionMerge,
    setRelatedInvestorsOptionMerge,
    avatarFile,
    setAvatarFile,
    fileUploadingSnackBar,
    setFileUploadingSnackBar,
    openMerger,
    setOpenMerger,
    openAddLocation,
    setOpenAddLocation,
    teamsToMergePreDraft,
    setTeamsToMergePreDraft,
    contactsPreDraft,
    setContactsPreDraft,
    fetchingTeamsToMergePreDraft,
    setFetchingTeamsToMergePreDraft,
    pwtType,
    setPwtType,
    loading,
    setLoading,

    // Estados de merge relations
    mergeRelations,
    setMergeRelations,
    mergedIn,
    setMergedIn,
    fetchingMergeRelations,
    setFetchingMergeRelations,

    // Custom hooks
    adminNotes,
    rejectionsNotes,
    email,
    resetEmail,
    validateEmail,
    url,
    resetUrl,
    validateUrl,

    // Estados de opciones
    optionsData,
    setOptionsData,

    // Estados de selección de contactos
    selectedContacts,
    setSelectedContacts,
    selectedCrds,
    setSelectedCrds,
    selectedFormerContacts,
    setSelectedFormerContacts,
    selectedFormerCrds,
    setSelectedFormerCrds,
    formers,
    setFormers,
    openMigrateModal,
    setOpenMigrateModal,
    locationSuggestion,
    setLocationSuggestion,

    // Valores computados
    dupWarnings,
    warningTeams,
    canUpdateTeamParent,
    canChangeStageAux,

    // Handlers de selección
    handleSelectOne,
    handleSelectOneFormer,
    handleSelectAll,
    handleSelectAllFormers,

    // Funciones de fetch
    fetchWarnings,
    fetchNews,
    fetchMergeRelations,
    fetchTeamsToMergePreDraft,
    handleFetchOptions,
    handleFetchContacts,

    // Handlers de paginación
    handleChangeRowsPerPage,
    handleChangeRowsPerPageFormer,
    handleChangeRowsPerPagePotential,
    handleDataFetchPage,
    handleDataFetchPageFormer,
    handleDataFetchPagePotential,

    // Handlers de ordenamiento
    handleSort,
    handleSortFormer,
    handleSortPotential,

    // Handlers de data fetch
    handleDataFetch,
    handleDataFormerFetch,
    handleDataPotentialFetch,

    // Handlers de toggle
    toggleExpandFormInfo,
    toggleExpandTeamMembers,
    toggleExpandPotentialTeamMembers,

    // Handlers de modal
    handleOpenContactModal,
    handleCloseContactModal,

    // Handlers de notas
    onOpenNotes,
    onOpenRejections,
    handleReject,

    // Handlers CRUD
    handleOnChangeInField,
    handleCreatePrivateWealthTeam,
    handleCloseFileSnackbar,
    handleUpdatePrivateWealthTeam,

    // Handlers de autocomplete
    handleSpecialtiesChange,
    handleInvestmentUtilizedChange,
    handleClientTypesChange,
    handleOnChange,
    handleOnChangeAutocomplete,

    // Handlers de stage
    handleSubmit,
    markAsReviewed,
    handleUnmerge,
    markAsComplete,
    publish,
    reject,
    createNewGroup,
    markAsBranchLocation,
    ignorePreDraft,

    // Handlers de contactos
    handleContactChange,
    handleConfirmPreview,
    handleInvestorChange,
    handlePwtTypeChange,
    handleAddContactToTeam,
    afterAddContactToTeam,

    // Handlers de team members
    handleRemoveTeamMember,
    handleRemoveTeamMembers,
    handleRemoveFormerTeamMembers,
    handleReviveTeamMember,
    handleAddToTeamMember,
    handleRemoveFormerTeamMember,

    // Handlers de migración
    handleOpenMigrateModal,
    handleMigrate,

    // Handlers de archivos
    handleChangeFileInput,

    // Handlers de merge
    openTeamMerger,
    closeTeamMerger,
    openAddLocationMerge,
    closeAddLocationMerge,
    mergeTeams,
    handleManageLocation,
    updateWarnings,

    // Handler de cierre
    handleOnClose,

    // Otros handlers
    openChangeLogs,
    handleCloseInactiveModal,
    onRegenerateAiBio,
    handleOnChangeNotes,
    handleRemoveLocation,

    // Funciones auxiliares
    investors,
    getSelectedOptions,

    // Validaciones
    canChangeStage,
    hasLocationError,
    hasLocationDups,
    hasHistoricalBlueprintError,
    hasHistoricalBlueprintDups,

    loadingChangeLogs,
  } = usePrivateWealthTeamForm({
    session,
    team,
    activeData,
    onClose,
    open,
    afterCreate,
    onDataUpdate,
    itemIndex,
    goBack,
    fetchPrivateWealthTeams,
    itemsInQueue,
    hasQueue,
    snackbar,
    draftData,
    fromWarnings,
    onChangeNotes
  });

  const firmInfoAumAggregation = getFirmAumAggregationInfo(aumAggregations);
  const teamNameExceedsMax = (teamFirmInfo?.team_name || '').length > TEAM_NAME_MAX_LENGTH;

  return (
    <React.Fragment>
      <Modal
        onClose={handleOnClose}
        open={open}
      >
        <Card
          className={classes.root}
        >
          <CardHeader
            title={
              <Box component={"div"}>
                <div className={classes.cardHeaderContainer}>
                  <Box component={"span"} style={{ display: "inline-flex", gridColumnGap: "10px" }}>
                    {
                      goBack ?
                        <NavLink to={goBack}><ArrowBack /></NavLink> :
                        <></>
                    }
                    <span style={{ display: "flex", alignItems: "center" }}>
                      {
                        team ?
                          team.team_name :
                          'New Wealth Team'
                      }
                      {
                        team && status === 'fetched' ?
                          <ActiveChip active={!teamFirmInfo.hidden} negative={"Hidden"} positive={"Live"} /> :
                          null
                      }
                    </span>
                    {
                      hasQueue ?
                        <>
                          <Divider orientation="vertical" flexItem={true} />
                          <Box component={"span"}>
                            <ChipLabel label={`In Queue: ${itemsInQueue}`} className={"colored-element create"} />
                          </Box>
                        </> :
                        <></>
                    }
                  </Box>
                  {
                    team?.stage !== 'pre_draft' && activeData ?
                      <>
                        <RegenerateBio
                          show={session.isSuperAdmin || session.inHouseResearch}
                          classes={{ label: classes.buttonLabel, root: classes.buttonMarginRight }}
                          onRegenerate={onRegenerateAiBio}
                        />
                        <Button
                          variant="outlined"
                          classes={{ label: classes.buttonLabel, root: classes.buttonMarginRight }}
                          onClick={() => setOpenMarkAsInactiveModal(true)}
                        >
                          Mark As Inactive
                        </Button>
                      </> :
                      team?.stage !== 'pre_draft' &&
                      <Button
                        variant="outlined"
                        classes={{ label: classes.buttonLabel, root: classes.buttonMarginRight }}
                        onClick={() => setOpenMarkAsInactiveModal(true)}
                      >
                        Why is Inactive
                      </Button>
                  }
                  {
                    team &&
                    <Button
                      variant="outlined"
                      classes={{ label: classes.buttonLabel, root: classes.buttonMarginRight }}
                      onClick={onOpenNotes}
                    >
                      Notes
                    </Button>
                  }
                  {
                    team &&
                    <Button
                      variant="outlined"
                      classes={{ label: classes.buttonLabel, root: classes.buttonMarginRight }}
                      onClick={openChangeLogs}
                      disabled={loadingChangeLogs}
                    >
                      {loadingChangeLogs ? 'Loading...' : 'Changes'}
                    </Button>
                  }
                </div>
                <div className={classes.cardHeaderContainer}>
                  <DuplicateTeamsWarning warnings={dupWarnings} team={team} />
                </div>
              </Box>
            } />

          <Divider />
          <CardContent
            style={{
              height: "calc(100% - 188px)",
              overflowY: "auto"
            }}
          >
            <Card>
              <CardHeader className='cursor-pointer' title={
                <span><span>Firm Info</span><span className='float-right'>
                  {
                    expandFirmInfo ?
                      <ExpandLess /> :
                      <ExpandMore />
                  }
                </span></span>} onClick={toggleExpandFormInfo}
              />
              <Divider />
              <CardContent style={{ padding: `${expandFirmInfo ? '20px' : 0}` }}>
                <Collapse in={expandFirmInfo}>
                  <Grid container spacing={3}>
                    <Grid item md={12} xs={12}>
                      <div className='user-avatar-container'>
                        <Avatar src={teamFirmInfo?.photoPreview} className="photo" alt="rep-avatar" />
                        <DropzoneArea
                          acceptedFiles={['image/*']}
                          dropzoneText={"Upload Image"}
                          Icon={CameraAlt}
                          dialogTitle={"Uploading the file to AWS"}
                          filesLimit={1}
                          showPreviews={false}
                          showAlerts={false}
                          onDrop={(files) => handleChangeFileInput(files)}
                          styles={{ dropzone: { minHeight: 200, maxHeight: 250 } }}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <SingleAsyncAutoComplete
                          fetch={investors}
                          handleOnChange={(value) => value ? handleInvestorChange(value) : null}
                          value={
                            teamFirmInfo?.crd_id ?
                              {
                                id: teamFirmInfo?.crd_id,
                                name: `${teamFirmInfo?.firm_name} (${teamFirmInfo?.crd_id})`
                              } :
                              ''
                          }
                          field={'crd_id'}
                          label={"Parent Company"}
                          disabled={!!team && !canUpdateTeamParent}
                        />
                        <span>{errorMessage['crd_id']}</span>
                      </FormControl>
                    </Grid>
                    {
                      optionsData.pwtTypes && <Grid item xs={12}>
                        <FilterAutoComplete
                          options={optionsData.pwtTypes}
                          handleOnChange={(value) => value ? handlePwtTypeChange(value) : null}
                          selected={pwtType}
                          label={'Type'}
                          field={'pwt_type'}
                        />
                      </Grid>
                    }
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <TextField
                          label="Team Name"
                          variant="outlined"
                          name='team_name'
                          required
                          inputProps={{ maxLength: 500 }}
                          value={teamFirmInfo.team_name || ''}
                          onChange={(e) => handleOnChange(e)}
                        />
                        <span>{errorMessage['team_name']}</span>
                        {
                          teamNameExceedsMax &&
                          <Box p={1}>
                          <span style={{ color: '#b71c1c' }}>
                            {`Warning: Team Name Exceeds ${TEAM_NAME_MAX_LENGTH} Characters`}
                          </span>
                          </Box>
                        }
                      </FormControl>
                    </Grid>
                    {
                      team?.stage !== 'pre_draft' &&
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            label="Team Bio"
                            variant="outlined"
                            name={'team_bio'}
                            onChange={(e) => handleOnChange(e)}
                            value={teamFirmInfo.team_bio || ''}
                            multiline={true}
                            maxRows={16}
                            minRows={8}
                          />
                        </FormControl>
                      </Grid>
                    }
                    {
                      (session.isSuperAdmin || session.inHouseResearch) && team?.stage !== 'pre_draft' &&
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            label="AI Bio"
                            variant="outlined"
                            name={'ai_bio'}
                            value={teamFirmInfo.ai_bio || ''}
                            multiline={true}
                            disabled
                            maxRows={16}
                            minRows={8}
                          />
                        </FormControl>
                      </Grid>
                    }
                    <Grid item xs={12}>
                      <UrlTextField
                        variant="outlined"
                        {...url}
                        label={"URL"}
                        name={"url"}
                        maxLength={150}
                      />
                    </Grid>
                    {team && team.stage !== 'pre_draft' &&
                      <>
                        <Grid item xs={12}>
                          <TextInput
                            {...email}
                            label={"Email"}
                            name={"email"}
                            maxLength={255}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <TextField
                              disabled
                              label='AUM'
                              variant='outlined'
                              name='total_aum'
                              value={firmInfoAumAggregation.total_aum ?? ''}
                              InputLabelProps={{ shrink: firmInfoAumAggregation.total_aum != null }}
                              InputProps={{ inputComponent: CurrencyFormatter }}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <TextField
                              disabled
                              label='Minimum Account Size'
                              variant='outlined'
                              name='minimum_account_size'
                              value={firmInfoAumAggregation.minimum_account_size ?? ''}
                              InputLabelProps={{ shrink: firmInfoAumAggregation.minimum_account_size != null }}
                              InputProps={{ inputComponent: CurrencyFormatter }}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          {optionsData.services && <FilterAutoComplete
                            options={optionsData.services}
                            handleOnChange={(value) => handleSpecialtiesChange(value)}
                            selected={selectedSpecialties}
                            label={'Services Provided'}
                            field={'specialties'}
                            multiple
                          />
                          }
                        </Grid>

                        <Grid item xs={12}>
                          {optionsData.investmentUtilized &&
                            <FilterAutoComplete
                              options={optionsData.investmentUtilized}
                              handleOnChange={(value) => handleInvestmentUtilizedChange(value)}
                              selected={selectedInvestmentUtilized}
                              label={'Investments Utilized'}
                              field={'investments_utilized'}
                              multiple
                            />
                          }
                        </Grid>
                        <Grid
                          item

                          xs={12}
                        >

                          {optionsData.clientTypes && <FilterAutoComplete
                            options={optionsData.clientTypes}
                            handleOnChange={(value) => handleClientTypesChange(value)}
                            selected={selectedClientTypes}
                            label={'Client Types'}
                            field={'client_types'}
                            multiple
                          />
                          }
                        </Grid>

                      </>
                    }

                  </Grid>
                </Collapse>
                <Divider />
              </CardContent>
              <Divider />
            </Card>

            {team &&
              <React.Fragment>

                <HistoricalBlueprintsSection
                  open={!!goBack}
                  handleOnChangeInField={handleOnChangeInField}
                  team={teamFirmInfo}
                  stage={team.stage}
                  handleOpenMaTransaction={handleOpenMaTransaction}
                />

                {team.stage !== 'pre_draft' &&
                  <Card style={{ marginTop: '24px' }}>
                    <CardHeader className='cursor-pointer' title={
                      <span><span>Team Members</span><span className='float-right'>
                        {
                          expandTeamMembers ?
                            <ExpandLess />
                            :
                            <ExpandMore />
                        }
                      </span></span>} onClick={toggleExpandTeamMembers} />
                    <Divider />
                    <CardContent style={{ padding: `${expandTeamMembers ? '20px' : 0}`, overflowY: 'auto' }}>
                      <Collapse in={expandTeamMembers}>
                        <SingleAsyncAutoComplete
                          fetch={handleFetchContacts}
                          handleOnChange={(value) => value ? handleContactChange(value) : null}
                          value={selectedContact}
                          field={'ria_contacts'}
                          label={"Contact"}
                        />
                        <Paper>
                          <TableContainer className='table-container' style={{ marginTop: '20px' }}>
                            <Table stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selectedContacts.length > 0 && selectedContacts.length === teamMembers.length}
                                      indeterminate={selectedContacts.length > 0 && selectedContacts.length < teamMembers.length}
                                      color="primary"
                                      onChange={handleSelectAll}
                                    />
                                  </TableCell>
                                  {headTeamMemberCells.map((headCell) => (
                                    headCell.nonSortable ? (
                                      <TableCell classes={{ root: classes.tableHeader }} key={headCell.id}>
                                        <span>
                                          {headCell.label}
                                        </span>
                                      </TableCell>
                                    )
                                      :
                                      <TableCell key={headCell.id}>
                                        {
                                          <TableSortLabel
                                            active={sortBy === headCell.id}
                                            direction={sortBy === headCell.id ? sortDirection : 'asc'}
                                            onClick={(event) => handleSort(event, headCell.id)}
                                            classes={{ root: classes.tableHeader }}
                                          >
                                            {headCell.label}
                                            {
                                              sortBy === headCell.id ? (
                                                <span className={classes.visuallyHidden}>
                                                </span>
                                              ) : null
                                            }
                                          </TableSortLabel>
                                        }
                                      </TableCell>
                                  ))}
                                  {selectedContacts.length > 0 ?
                                    <TableCell>
                                      <Button
                                        style={{ color: 'white', backgroundColor: '#224edf', marginRight: '12px' }}
                                        onClick={() => {
                                          handleOpenMigrateModal(false)
                                        }}>Migrate</Button>
                                      <Button style={{ color: 'white', backgroundColor: '#224edf' }}
                                        onClick={handleRemoveTeamMembers}>Mark As Former</Button>
                                    </TableCell>
                                    :
                                    <>
                                      <TableCell />
                                    </>
                                  }
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {
                                  status !== 'fetched' || !teamMembers ?
                                    <TableRow><TableCell colspans={5}>Loading...</TableCell></TableRow>
                                    :
                                    teamMembers.map((member, index) =>
                                      <UnifiedTeamMemberRow
                                        key={member.id}
                                        member={member}
                                        type="active"
                                        index={index}
                                        selected={selectedContacts}
                                        onSelect={handleSelectOne}
                                        onOpenContact={handleOpenContactModal}
                                        onPrimaryAction={handleRemoveTeamMember}
                                      />
                                    )}
                              </TableBody>

                            </Table>
                          </TableContainer>
                          <TablePagination
                            component="div"
                            count={totalItems}
                            labelDisplayedRows={(data) => `${data.from}-${data.to === -1 ? data.count : data.to} of ${data.count !== -1 ? formatNumber(data.count) : data.to}`}
                            onPageChange={handleDataFetchPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            page={currentPage}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[20, 50, 100, 250, 500, 1000, 2000]} />
                        </Paper>
                        <div style={{ margin: '20px 0' }}>Former Team Members</div>
                        <Paper>
                          <TableContainer className='table-container'>
                            <Table stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selectedFormerContacts.length > 0 && selectedFormerContacts.length === formerTeamMembers.length}
                                      indeterminate={selectedFormerContacts.length > 0 && selectedFormerContacts.length < formerTeamMembers.length}
                                      color="primary"
                                      onChange={handleSelectAllFormers}
                                    />
                                  </TableCell>
                                  {headFormerTeamMemberCells.map((headCell) => (
                                    headCell.nonSortable ? (
                                      <TableCell classes={{ root: classes.tableHeader }} key={headCell.id}>
                                        <span>
                                          {headCell.label}
                                        </span>
                                      </TableCell>
                                    )
                                      :
                                      <TableCell key={headCell.id}>
                                        {
                                          <TableSortLabel
                                            active={formerSortBy === headCell.id}
                                            direction={formerSortBy === headCell.id ? formerSortDirection : 'asc'}
                                            onClick={(event) => handleSortFormer(event, headCell.id)}
                                            classes={{ root: classes.tableHeader }}
                                          >
                                            {headCell.label}
                                            {
                                              sortBy === headCell.id ? (
                                                <span className={classes.visuallyHidden}>
                                                </span>
                                              ) : null
                                            }
                                          </TableSortLabel>
                                        }
                                      </TableCell>
                                  ))}
                                  {selectedFormerContacts.length > 0 ?
                                    <>
                                      <TableCell>
                                        <Button style={{ color: 'white', backgroundColor: '#224edf' }}
                                          onClick={handleRemoveFormerTeamMembers}>Delete</Button>
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          style={{ color: 'white', backgroundColor: '#224edf', marginRight: '12px' }}
                                          onClick={() => {
                                            handleOpenMigrateModal(true)
                                          }}>Migrate</Button>
                                      </TableCell>
                                    </> :
                                    <>
                                      <TableCell />
                                    </>
                                  }
                                  <TableCell />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {
                                  status !== 'fetched' ?
                                    <TableRow><TableCell colspans={5}>Loading...</TableCell></TableRow>
                                    :
                                    formerTeamMembers.map((member, index) =>
                                      <UnifiedTeamMemberRow
                                        key={member.id}
                                        member={member}
                                        type="former"
                                        index={index}
                                        selected={selectedFormerContacts}
                                        onSelect={handleSelectOneFormer}
                                        onPrimaryAction={handleReviveTeamMember}
                                        onSecondaryAction={handleRemoveFormerTeamMember}
                                      />
                                    )}
                              </TableBody>

                            </Table>
                          </TableContainer>
                          <TablePagination
                            component="div"
                            count={totalItemsFormer}
                            labelDisplayedRows={(data) => `${data.from}-${data.to === -1 ? data.count : data.to} of ${data.count !== -1 ? formatNumber(data.count) : data.to}`}
                            onPageChange={handleDataFetchPageFormer}
                            onRowsPerPageChange={handleChangeRowsPerPageFormer}
                            page={currentPageFormer}
                            rowsPerPage={rowsPerPageFormer}
                            rowsPerPageOptions={[20, 50, 100, 250, 500, 1000, 2000]}
                          />
                        </Paper>
                      </Collapse>
                    </CardContent>
                    <Divider />
                  </Card>
                }

                {team.stage !== 'pre_draft' &&
                  <Card style={{ marginTop: '24px' }}>
                    <CardHeader className='cursor-pointer' title={
                      <span><span>Potential Team Members</span><span className='float-right'>
                        {
                          expandPotentialTeamMembers ?
                            <ExpandLess />
                            :
                            <ExpandMore />
                        }
                      </span></span>} onClick={toggleExpandPotentialTeamMembers} />
                    <Divider />
                    <CardContent style={{ padding: `${expandPotentialTeamMembers ? '20px' : 0}`, overflowY: 'auto' }}>
                      <Collapse in={expandPotentialTeamMembers}>
                        <Paper>
                          <TableContainer className='table-container'>
                            <Table stickyHeader>
                              <TableHead>
                                <TableRow>
                                  {headTeamPotentialCells.map((headCell) => (
                                    <TableCell key={headCell.id}>
                                      {
                                        <TableSortLabel
                                          active={potentialSortBy === headCell.id}
                                          direction={potentialSortBy === headCell.id ? potentialSortDirection : 'asc'}
                                          onClick={(event) => handleSortPotential(event, headCell.id)}
                                          classes={{ root: classes.tableHeader }}
                                        >
                                          {headCell.label}
                                          {
                                            sortBy === headCell.id ? (
                                              <span className={classes.visuallyHidden}>
                                              </span>
                                            ) : null
                                          }
                                        </TableSortLabel>
                                      }
                                    </TableCell>
                                  ))}
                                  <TableCell />
                                  <TableCell />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {
                                  status !== 'fetched' ?
                                    <TableRow><TableCell colspans={5}>Loading...</TableCell></TableRow>
                                    :
                                    potentialTeamMembers.map((member, index) =>
                                      <UnifiedTeamMemberRow
                                        key={member.id}
                                        member={member}
                                        type="potential"
                                        index={index}
                                        onOpenContact={handleOpenContactModal}
                                        onPrimaryAction={handleAddToTeamMember}
                                      />
                                    )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <TablePagination
                            component="div"
                            count={totalItemsPotential}
                            labelDisplayedRows={(data) => `${data.from}-${data.to === -1 ? data.count : data.to} of ${data.count !== -1 ? formatNumber(data.count) : data.to}`}
                            onPageChange={handleDataFetchPagePotential}
                            onRowsPerPageChange={handleChangeRowsPerPagePotential}
                            page={currentPagePotential}
                            rowsPerPage={rowsPerPagePotential}
                            rowsPerPageOptions={[20, 50, 100, 250, 500, 1000, 2000]}
                          />
                        </Paper>
                      </Collapse>
                    </CardContent>
                    <Divider />
                  </Card>
                }
                {
                  team.stage !== 'pre_draft' &&
                  <WarningsSection
                    hasChanges={hasChanges}
                    warnings={warnings}
                    setWarnings={setWarnings}
                    open={!!goBack}
                    fetching={fetchingWarnings}
                    from={goBack ? goBack : "/admin/live_data/private_wealth_teams"}
                  />
                }
                {
                  team.stage !== 'pre_draft' &&
                  <AccoladesSection
                    open={!!goBack}
                    accolades={accolades}
                    fetching={fetchingAccolades}
                    excludedAccolades={excludedAccolades}
                    fetchExcludedAccolades={fetchExcludedAccolades}
                    handleAddAccoladeToTeam={handleAddAccoladeToTeam}
                    handleRemoveAccoladeFromTeam={handleRemoveAccoladeFromTeam}
                  />
                }
                {
                  team.stage === 'pre_draft' &&
                  <PreDraftContactsSection
                    contacts={contactsPreDraft}
                    fetching={fetchingTeamsToMergePreDraft}
                    open={!!goBack} handleOpenContactModal={handleOpenContactModal}
                  />
                }
                <LocationsSection
                  open={!!goBack}
                  handleOnChangeInField={handleOnChangeInField}
                  locationOptions={optionsData}
                  team={teamFirmInfo}
                  showAddLocation={team.stage !== 'pre_draft'}
                  onRemoveLocation={handleRemoveLocation}
                />
                {
                  team.stage !== 'pre_draft' &&
                  <AumAggregationsSection
                    open={!!goBack}
                    aumAggregations={aumAggregations.filter(item => item._destroy !== true)}
                    handleChangeAumAggregation={handleChangeAumAggregation}
                    handleBlurAumAggregation={handleBlurAumAggregation}
                    handleAddAumAggregation={handleAddAumAggregation}
                    handleRemoveAumAggregation={handleRemoveAumAggregation}
                    fetching={fetchingAumAggregations}
                  />
                }
                {
                  team.stage !== 'pre_draft' &&
                  <PrivateWealthTeamNews
                    news={news}
                    fetchNews={fetchNews}
                    newsTypes={newsTypes}
                    riaInvestorParent={riaInvestorParent}
                    open={!!goBack}
                    fetching={fetchingNews}
                    team={team}
                  />
                }
                {
                  team.stage !== 'pre_draft' && (mergedIn || mergeRelations.length > 0) &&
                  <MergeRelations
                    mergeRelations={mergeRelations}
                    mergedIn={mergedIn}
                    open={!!goBack}
                    fetching={fetchingMergeRelations}
                    team={team}
                  />
                }
              </React.Fragment>
            }

          </CardContent>
          <Divider />
          <CardActions>
            <Button onClick={onClose}>Close</Button>
            <Tooltip title={teamNameExceedsMax ? `Team Name Exceeds ${TEAM_NAME_MAX_LENGTH} Characters` :
              canChangeStageAux.includes(DUPLICATE_LOCATION_ERROR) ? DUPLICATE_LOCATION_ERROR :
              canChangeStageAux.includes(VALIDATE_LOCATION_ERROR) ? VALIDATE_LOCATION_ERROR :
                canChangeStageAux.includes(DUPLICATE_HISTORICAL_BLUEPRINT_ERROR) ? DUPLICATE_HISTORICAL_BLUEPRINT_ERROR :
                  canChangeStageAux.includes(VALIDATE_HISTORICAL_BLUEPRINT_ERROR) ? VALIDATE_HISTORICAL_BLUEPRINT_ERROR :
                    canChangeStageAux.includes(MISSING_PRINCIPAL_ERROR) ? MISSING_PRINCIPAL_ERROR : ''}>
              <span>
                <Button color="primary" variant="contained" onClick={handleSubmit}
                  disabled={!hasChanges || teamNameExceedsMax || canChangeStageAux.includes(DUPLICATE_LOCATION_ERROR) || canChangeStageAux.includes(MISSING_PRINCIPAL_ERROR) || canChangeStageAux.includes(VALIDATE_LOCATION_ERROR) ||
                    canChangeStageAux.includes(DUPLICATE_HISTORICAL_BLUEPRINT_ERROR) || canChangeStageAux.includes(VALIDATE_HISTORICAL_BLUEPRINT_ERROR) ||
                    loading}
                >Save</Button>
              </span>
            </Tooltip>
            {team && draftData && (team.stage === 'draft_incomplete' || team.stage === 'draft_rejected') &&
              <Tooltip title={teamNameExceedsMax ? `Team Name Exceeds ${TEAM_NAME_MAX_LENGTH} Characters` : canChangeStageAux.length === 0 ? '' : canChangeStageAux.join('. ')}>
                <span>
                  <Button color="primary" variant="contained" onClick={markAsComplete} disabled={teamNameExceedsMax || canChangeStageAux.length > 0 || loading}>Save & Mark Complete</Button>
                </span>
              </Tooltip>
            }
            {
              (session.isSuperAdmin || session.inHouseResearch) &&
              team && draftData && team.stage === 'draft_complete' &&
              <Tooltip title={teamNameExceedsMax ? `Team Name Exceeds ${TEAM_NAME_MAX_LENGTH} Characters` : canChangeStageAux.length === 0 ? '' : canChangeStageAux.join('. ')}>
                <span>
                  <Button color="primary" variant="contained" onClick={publish} disabled={teamNameExceedsMax || canChangeStageAux.length > 0}>Publish</Button>
                </span>
              </Tooltip>
            }
            {team && draftData && team.stage === 'draft_complete' &&
              <span>
                <Button color="primary" variant="contained" onClick={onOpenRejections}>Reject</Button>
              </span>
            }
            {team && (!draftData || fromWarnings) && !hasQueue && <Button color="primary" variant="contained" onClick={markAsReviewed} disabled={hasChanges || loading}>Mark as Reviewed</Button>}
            {team && team.merged && !team.merge_survivor_live && (session.isSuperAdmin || session.inHouseResearch) &&
              <Button color="secondary" variant="contained" onClick={handleUnmerge} disabled={loading}>Unmerge</Button>
            }
            {team && dupWarnings.length > 0 ? <Button color="primary" variant="contained" onClick={openTeamMerger}>Merge</Button> : null}
            {team && draftData && team.stage === 'pre_draft' &&
              <>
                <span>
                  <Button color="primary" variant="contained" onClick={openAddLocationMerge}>Add Location</Button>
                </span>
                <span>
                  <Button color="primary" variant="contained" onClick={createNewGroup}>Create New Group</Button>
                </span>
                <span>
                  <Button color="primary" variant="contained" onClick={markAsBranchLocation}>Mark as Branch</Button>
                </span>
                <span>
                  <Button color="primary" variant="contained" onClick={ignorePreDraft}>Ignore</Button>
                </span>
              </>
            }
          </CardActions>
        </Card>
      </Modal>
      {showSnackBar &&
        <CustomSnackbar
          open={showSnackBar}
          closeHandler={() => setShowSnackBar(false)}
          message={errorMessage}
          linkText={linkText}
          linkAction={linkAction}
          stayOpen={true}
          options={{
            severity: 'warning'
          }}
        />
      }
      {
        openChanges && (
          <ChangeLogModal open={openChanges} onClose={() => setOpenChanges(false)} changes={changes} changedBy={"FintrX Importer"} />
        )
      }
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Card style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
          maxHeight: '100%',
          overflowY: 'auto',
          width: '20%',
        }}>
          <CardContent style={{ display: 'flex', flexDirection: 'column' }}>
            <p>Do you want to add this contact to Wealth Team?</p>
          </CardContent>
          <Divider />
          <CardActions style={{ justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpenPreview(true)}>VIEW PREVIEW FIRST</Button>
            <Button onClick={() => setOpenModal(false)}>NO</Button>
            <Button onClick={() => handleConfirmPreview()}>YES</Button>
          </CardActions>
        </Card>
      </Modal>
      {openPreview &&
        <Modal open={openPreview} onClose={() => setOpenPreview(false)}>
          <Card style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
            maxHeight: '100%',
            overflowY: 'auto',
            width: '70%',
            height: '95%',
          }}>
            <CardHeader title='Contact Preview' />
            <CardContent style={{
              pointerEvents: 'none',
            }}>
              <AdminEditContactContainer
                contactId={selectedContact.crd_id}
                onClose={() => setOpenPreview(false)}
                open={openPreview}
                onDataUpdate={() => null}
                fromInvestorModal={false}
                readOnly={true}
              />
            </CardContent>
          </Card>
        </Modal>
      }
      {
        openMarkAsInactiveModal &&
        <PwtMarkAsInactiveModal
          teamId={team.id}
          handleCloseModalAndFetch={handleCloseInactiveModal}
          handleCloseModal={() => setOpenMarkAsInactiveModal(false)}
          openFeedbackModal={openMarkAsInactiveModal}
          active={activeData}>
        </PwtMarkAsInactiveModal>
      }
      {
        openMigrateModal &&
        <MigrateModal
          open={openMigrateModal}
          onClose={() => setOpenMigrateModal(false)}
          handleMigrate={handleMigrate}
          adminUserSession={session}
          isFormer={formers}
          investors={relatedInvestorsOptionMerge}
        />
      }
      <NotesDialog
        label={"Add a Note"}
        body={""}
        adminNotes={adminNotes}
        onChangeNotes={handleOnChangeNotes}
      />
      <PwgRejectedDialog
        rejectionsNotes={rejectionsNotes}
        handleReject={handleReject}
      />
      <TeamMerger
        open={openMerger}
        close={closeTeamMerger}
        team={team}
        teams={warningTeams}
        handleMerge={mergeTeams}
      />
      {
        team?.stage === 'pre_draft' ?
          <TeamLocationAddReplace
            open={openAddLocation}
            close={closeAddLocationMerge}
            team={team}
            teams={teamsToMergePreDraft}
            handleManageLocation={handleManageLocation}
          /> :
          <TeamLocationMerger
            open={openAddLocation}
            close={closeAddLocationMerge}
            team={team}
            teams={teamsToMergePreDraft}
            handleManageLocation={handleManageLocation}
          />
      }
      {
        fileUploadingSnackBar.showSnackBar ?
          <CustomSnackbar
            open={fileUploadingSnackBar.showSnackBar}
            closeHandler={handleCloseFileSnackbar}
            message={fileUploadingSnackBar.snackBarMessage}
            options={{ severity: 'warning' }}
            stayOpen={fileUploadingSnackBar.stayOpen}
          /> :
          null
      }
      {
        openContactModal &&
        <AdminEditContactContainer
          contactId={selectedContactCrdId}
          onClose={handleCloseContactModal}
          open={openContactModal}
          fromInvestorModal={false}
        />
      }

      {/* M&A Transaction Modal */}
      {openMaTransactionModal && selectedMaTransactionId && (
        <React.Suspense fallback={<CircularProgress />}>
          <MATransactionForm
            itemId={selectedMaTransactionId}
            onClose={handleCloseMaTransaction}
            open={openMaTransactionModal}
            onDataUpdate={() => { }}
            afterCreate={() => { }}
            generalClasses={{}}
            outsideSnackbar={snackbar}
            setSelectedItemId={setSelectedMaTransactionId}
          />
        </React.Suspense>
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        children={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmColor={confirmDialog.confirmColor}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
      />
    </React.Fragment>
  );
};

export default PrivateWealthTeamForm;
